import React, { Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AlertTriangle } from 'lucide-react'
import { AuthProvider } from './contexts/AuthContext'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { useAuthState } from './hooks/useAuthState'
import { Button } from './components/ui/button'
import { SubscriptionGuard } from './components/SubscriptionGuard'

// Authentication pages
const AuthPage = React.lazy(() => import('./pages/auth/AuthPage').then(module => ({ default: module.AuthPage })))
const InviteAcceptPage = React.lazy(() => import('./pages/auth/InviteAcceptPage').then(module => ({ default: module.InviteAcceptPage })))
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage').then(module => ({ default: module.ResetPasswordPage })))
const EmailVerificationPage = React.lazy(() => import('./pages/auth/EmailVerificationPage').then(module => ({ default: module.EmailVerificationPage })))

// Public pages (no authentication required)
const PublicQuotePage = React.lazy(() => import('./pages/quotes/PublicQuotePage').then(module => ({ default: module.PublicQuotePage })))

// Main application pages
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage').then(module => ({ default: module.DashboardPage })))
const FacilitiesPage = React.lazy(() => import('./pages/facilities/FacilitiesPage').then(module => ({ default: module.FacilitiesPage })))
const ServicesPage = React.lazy(() => import('./pages/services/ServicesPage').then(module => ({ default: module.ServicesPage })))
const ServiceNewPage = React.lazy(() => import('./pages/services/ServiceNewPage').then(module => ({ default: module.ServiceNewPage })))
const ServiceTypeDetailPage = React.lazy(() => import('./pages/services/ServiceTypeDetailPage').then(module => ({ default: module.ServiceTypeDetailPage })))
const FacilityNewPage = React.lazy(() => import('./pages/facilities/FacilityNewPage').then(module => ({ default: module.FacilityNewPage })))
const FacilityDetailsPage = React.lazy(() => import('./pages/facilities/FacilityDetailsPage').then(module => ({ default: module.FacilityDetailsPage })))
const ClientsPage = React.lazy(() => import('./pages/clients/ClientsPage').then(module => ({ default: module.ClientsPage })))
const ClientDetailsPage = React.lazy(() => import('./pages/clients/ClientDetailsPage').then(module => ({ default: module.ClientDetailsPage })))
const QuotesPage = React.lazy(() => import('./pages/quotes/QuotesPage').then(module => ({ default: module.QuotesPage })))
const QuoteNewPage = React.lazy(() => import('./pages/quotes/QuoteNewPage').then(module => ({ default: module.QuoteNewPage })))
const QuoteViewPage = React.lazy(() => import('./pages/quotes/QuoteViewPage').then(module => ({ default: module.QuoteViewPage })))
const ReportsPage = React.lazy(() => import('./pages/reports/ReportsPage').then(module => ({ default: module.ReportsPage })))
const UserManagementPage = React.lazy(() => import('./pages/organization/UserManagementPage').then(module => ({ default: module.UserManagementPage })))
const OrganizationPage = React.lazy(() => import('./pages/organization/OrganizationPage').then(module => ({ default: module.OrganizationPage })))
const BillingPage = React.lazy(() => import('./pages/billing/BillingPage').then(module => ({ default: module.BillingPage })))
const SubscriptionSetupPage = React.lazy(() => import('./pages/billing/SubscriptionSetupPage').then(module => ({ default: module.SubscriptionSetupPage })))

// Layout component
const Layout = React.lazy(() => import('./components/layout/Layout').then(module => ({ default: module.Layout })))

/**
 * Main Application Component
 *
 * Handles:
 * - Route protection and navigation
 * - Authentication state management (via useAuthState hook)
 * - Loading states and error handling
 * - Special handling for invite and reset password pages
 */
function App() {
  const { user, loading, connectionError, isInvitePage, hasOrganization, isEmailVerified, clearAuth } = useAuthState()

  // Check if this is a public quote page - bypass authentication
  const isPublicQuotePage = window.location.pathname.startsWith('/shared/quote/')
  
  // Show public quote page without authentication requirement
  if (isPublicQuotePage) {
    return (
      <BrowserRouter>
        <Suspense fallback={
          <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        }>
          <Routes>
            <Route path="/shared/quote/:id" element={<PublicQuotePage />} />
            <Route path="*" element={<Navigate to="/shared/quote" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" />
      </BrowserRouter>
    )
  }

  // Loading state with manual clear option
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground mb-4">Loading...</p>
          {connectionError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md max-w-md">
              <p className="text-sm text-destructive">⚠️ {connectionError}</p>
            </div>
          )}
          <button
            onClick={clearAuth}
            className="text-sm text-primary hover:text-primary/80 underline"
          >
            Clear Auth & Reload
          </button>
        </div>
      </div>
    )
  }

  // Show authentication page for unauthenticated users (except invite pages)
  if (!user && !isInvitePage) {
    return (
      <>
        <AuthPage onSuccess={() => window.location.reload()} />
        <Toaster position="top-right" />
      </>
    )
  }

  // Show invite acceptance page without authentication requirement
  if (isInvitePage && !user) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/invite" element={<InviteAcceptPage />} />
          <Route path="*" element={<Navigate to="/invite" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    )
  }

  // Show reset password page without authentication requirement
  if (window.location.pathname === '/reset-password' && !user) {
    return (
      <BrowserRouter>
        <ResetPasswordPage />
        <Toaster position="top-right" />
      </BrowserRouter>
    )
  }

  // Show email verification page for unverified users
  if (user && !isEmailVerified) {
    return (
      <Suspense fallback={
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      }>
        <EmailVerificationPage
          email={user.email || ''}
          onSignOut={clearAuth}
        />
        <Toaster position="top-right" />
      </Suspense>
    )
  }

  // Show error page for orphaned users (authenticated but no org_id)
  // This should not happen in normal flows - indicates a bug or data issue
  if (user && hasOrganization === false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-6 text-center border border-border">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2 text-foreground">Account Issue</h1>
          <p className="text-muted-foreground mb-4">
            Your account is not linked to an organization. This may happen if your invite expired or there was a signup error.
          </p>
          <Button onClick={clearAuth} className="w-full">
            Sign Out & Try Again
          </Button>
          <p className="text-xs text-muted-foreground mt-4">
            Need help? Contact support@carequote.com
          </p>
        </div>
        <Toaster position="top-right" />
      </div>
    )
  }

  // Main authenticated application
  return (
    <BrowserRouter>
      <ErrorBoundary level="page" enableAutoRetry={true} maxRetries={3} showToast={true}>
        <AuthProvider>
          <Suspense fallback={
            <div className="min-h-screen bg-background flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading...</p>
              </div>
            </div>
           }>
             <Layout>
               <SubscriptionGuard>
                 <Routes>
                 {/* Dashboard */}
                 <Route path="/dashboard" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <DashboardPage />
                </ErrorBoundary>
              } />

              {/* Facility/Service Management */}
              <Route path="/facilities" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <FacilitiesPage />
                </ErrorBoundary>
              } />
              <Route path="/facilities/new" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <FacilityNewPage />
                </ErrorBoundary>
              } />
              <Route path="/facilities/:id" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <FacilityDetailsPage />
                </ErrorBoundary>
              } />
              <Route path="/services" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <ServicesPage />
                </ErrorBoundary>
              } />
              <Route path="/services/new" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <ServiceNewPage />
                </ErrorBoundary>
              } />
              <Route path="/services/:serviceType" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <ServiceTypeDetailPage />
                </ErrorBoundary>
              } />

              {/* Client Management */}
              <Route path="/clients" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <ClientsPage />
                </ErrorBoundary>
              } />
              <Route path="/clients/:id" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <ClientDetailsPage />
                </ErrorBoundary>
              } />

              {/* Quote Management */}
              <Route path="/quotes" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <QuotesPage />
                </ErrorBoundary>
              } />
              <Route path="/quotes/new" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <QuoteNewPage />
                </ErrorBoundary>
              } />
              <Route path="/quotes/:id" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <QuoteViewPage />
                </ErrorBoundary>
              } />
              <Route path="/quotes/:id/edit" element={
                <ErrorBoundary enableAutoRetry={true} maxRetries={2}>
                  <QuoteNewPage />
                </ErrorBoundary>
              } />

              {/* Reports & Analytics */}
              <Route path="/reports" element={
                <ErrorBoundary>
                  <ReportsPage />
                </ErrorBoundary>
              } />

              {/* Billing */}
              <Route path="/billing" element={
                <ErrorBoundary>
                  <BillingPage />
                </ErrorBoundary>
              } />
              <Route path="/billing/setup" element={
                <ErrorBoundary>
                  <SubscriptionSetupPage />
                </ErrorBoundary>
              } />

              {/* Organization Management */}
              <Route path="/organization" element={
                <ErrorBoundary>
                  <OrganizationPage />
                </ErrorBoundary>
              } />

              {/* User Management */}
              <Route path="/users" element={
                <ErrorBoundary>
                  <UserManagementPage />
                </ErrorBoundary>
              } />

              {/* Invite Handling */}
              <Route path="/invite" element={
                <ErrorBoundary>
                  <InviteAcceptPage />
                </ErrorBoundary>
              } />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </SubscriptionGuard>
            </Layout>
        </Suspense>
        <Toaster position="top-right" />
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  )
}

export default App